import { useEffect, useRef, useState } from 'react'

import { Box, Section } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Upload } from '@factorialco/f0-react/icons/app'
import {
  EngagementSettingsRootPageDocument,
  OneOnOnesMeetingsConfigCreateDocument,
  OneOnOnesMeetingsConfigFragment,
  OneOnOnesMeetingsConfigUpdateDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useImportOneOnOnesCsv } from 'modules/meetings/hooks/useImportOneOnOnesCsv'

import { ExpirationTimeSelector } from './components/ExpirationTimeSelector'
import { ImportResultsModal } from './components/ImportResultsModal'

interface OneOnOnesMeetingsConfigProps {
  settings?: OneOnOnesMeetingsConfigFragment
  loading: boolean
}

export function OneOnOnesMeetingsConfig({ settings, loading }: OneOnOnesMeetingsConfigProps) {
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const { id: meetingSettingId, transcriptExpirationTime } = settings ?? {}

  const [selectedValue, setSelectedValue] = useState<number>(transcriptExpirationTime ?? 17520)
  const [showImportResults, setShowImportResults] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { importCsv, loading: importLoading, importResult } = useImportOneOnOnesCsv()

  const [createOneOnOnesMeetingsConfig, { loading: loadingCreate }] = useMutation(
    OneOnOnesMeetingsConfigCreateDocument,
    {
      extractorFn: (data) => data?.meetingsMutations?.createMeetingsSetting,
      refetchQueries: [EngagementSettingsRootPageDocument],
    }
  )

  const [updateOneOnOnesMeetingsConfig, { loading: loadingUpdate }] = useMutation(
    OneOnOnesMeetingsConfigUpdateDocument,
    {
      extractorFn: (data) => data?.meetingsMutations?.updateMeetingsSetting,
    }
  )

  const handleCreate = async (value: number) => {
    const result = await createOneOnOnesMeetingsConfig({
      variables: {
        companyId: companyId,
        transcriptExpirationTime: value,
      },
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      successMessage()
      return true
    }

    errorMessage()
    return false
  }

  const handleUpdate = async (value: number) => {
    if (!meetingSettingId) return

    const result = await updateOneOnOnesMeetingsConfig({
      variables: {
        id: meetingSettingId,
        transcriptExpirationTime: value,
      },
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      successMessage()
      return true
    }

    errorMessage()
    return false
  }

  const successMessage = () => {
    app.setFlash({
      type: 'correct',
      message: i18n.t('engagement.settings.one_on_ones.expiration_time.success_message'),
    })
  }

  const errorMessage = () => {
    app.setFlash({
      type: 'wrong',
      message: i18n.t('engagement.settings.one_on_ones.expiration_time.error_message'),
    })
  }

  const handleChange = async (value: number) => {
    if (!meetingSettingId) {
      const created = await handleCreate(value)
      if (created) {
        setSelectedValue(value)
      }
      return
    }

    const updated = await handleUpdate(value)
    if (updated) {
      setSelectedValue(value)
    }
  }

  useEffect(() => {
    if (importResult) {
      setShowImportResults(true)
      if (importResult.errors.length === 0 && importResult.successfulImports > 0) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('meetings.one_on_ones.import.success'),
        })
      }
    }
  }, [importResult])

  const handleImportClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    try {
      await importCsv(file)
    } catch (error) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('meetings.one_on_ones.import.error'),
      })
    } finally {
      // Reset input so the same file can be uploaded again
      if (event.target) {
        event.target.value = ''
      }
    }
  }

  const AvailableExpirationTimes = [
    {
      value: 17520,
      label: i18n.t('engagement.settings.one_on_ones.expiration_time.options.2_years'),
    },
    {
      value: 4380,
      label: i18n.t('engagement.settings.one_on_ones.expiration_time.options.6_months'),
    },
    {
      value: 2190,
      label: i18n.t('engagement.settings.one_on_ones.expiration_time.options.3_months'),
    },
    {
      value: 720,
      label: i18n.t('engagement.settings.one_on_ones.expiration_time.options.1_month'),
    },
    {
      value: 24,
      label: i18n.t('engagement.settings.one_on_ones.expiration_time.options.1_day'),
    },
  ]

  return (
    <>
      <Section.TwoThirds
        illustration="clock"
        title={i18n.t('engagement.settings.one_on_ones.expiration_time.title')}
        description={i18n.t('engagement.settings.one_on_ones.expiration_time.description')}
      >
        <Box gap="s16" alignItems="flexEnd" justifyContent="flexEnd">
          {AvailableExpirationTimes.map((expirationTime) => (
            <ExpirationTimeSelector
              key={expirationTime.value}
              onClick={handleChange}
              value={expirationTime.value}
              label={expirationTime.label}
              selectedValue={selectedValue}
              loading={loadingCreate || loadingUpdate || loading}
            />
          ))}
        </Box>
      </Section.TwoThirds>

      <Section.TwoThirds
        illustration="file-upload"
        title={i18n.t('engagement.settings.one_on_ones.import.title')}
        description={i18n.t('engagement.settings.one_on_ones.import.description')}
      >
        <Box gap="s16" alignItems="flexEnd" justifyContent="center" height="full">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <F0Button
            variant="default"
            icon={Upload}
            onClick={handleImportClick}
            loading={importLoading}
            label={i18n.t('meetings.one_on_ones.import.button')}
          />
        </Box>
      </Section.TwoThirds>

      {showImportResults && (
        <ImportResultsModal
          isOpen={showImportResults}
          onClose={() => setShowImportResults(false)}
          results={importResult}
        />
      )}
    </>
  )
}
